import { Inject, Injectable } from '@angular/core';
import { defer, firstValueFrom, forkJoin, Observable, of, timer } from 'rxjs';
import { map, catchError, retry, switchMap, take } from 'rxjs/operators';
import { PatientFormsServicesToken, type SavePatientResult, type RxPatientData } from './patient-forms-services.token';
import { mapRxPatientDataToPatient, mapDtoToPatient, mapSearchItemToPatient } from './patient-mapping';
import { Patient, PatientFormsContext, PatientFormsEvent, isPatientModified } from '../state-machine/patient-forms.types';
import { checkCrossSystemMatches } from '../state-machine/cross-system-match';
import { DobFormatService } from './dob-format.service';
import { PatientLinkApiService, type SavedPatientDto, type SearchPatientsResponse } from './patient-link-api.service';
import { UNIFIED_PATIENTS_CONFIG, type UnifiedPatientsConfig } from './unified-patients-config.token';
import { UnifiedPatientsBiService } from '../observability/bi.service';
import { BI_FEATURE } from '../observability/bi-constants';
import { buildSaveDimensions } from '../observability/build-save-dimensions';

const CREATE_LINKED_PATIENT_INITIAL_DRY_RUN_DELAY_MS = 800; // Scanner only (`RxEditor` host URL); 0 otherwise
const CREATE_LINKED_PATIENT_RETRY_INTERVALS_MS = [20, 200, 2000];
// retry({ count: intervals.length }) = 1 initial attempt + intervals.length retries.

type SavedIteroPatient = NonNullable<SavePatientResult['savedIteroPatient']>;
type LinkedPatientIds = NonNullable<SavePatientResult['linkedPatient']>;

type LinkSuccess = {
	linked: LinkedPatientIds;
	resolvedIteroPatientId?: string;
};

type SendBack = (event: PatientFormsEvent) => void;

@Injectable()
export class PatientFormsServices extends PatientFormsServicesToken {
	constructor(
		private readonly api: PatientLinkApiService,
		private readonly dobFormat: DobFormatService,
		private readonly bi: UnifiedPatientsBiService,
		@Inject(UNIFIED_PATIENTS_CONFIG) private readonly config: UnifiedPatientsConfig
	) {
		super();
	}

	getRecentPatients(): Promise<Patient[]> {
		if (!this.api.clinicianId) {
			return Promise.resolve([]);
		}

		return firstValueFrom(
			this.api.getRecentPatients().pipe(
				map(res => (res.results ?? []).map(item => mapSearchItemToPatient(item))),
				catchError(() => of([]))
			)
		);
	}

	savePatient(context: PatientFormsContext, stateMatches: (path: string) => boolean): Observable<SavePatientResult> {
		const iTeroPatient = context.iTeroPatient;

		if (!iTeroPatient) {
			return of({ success: false });
		}

		const isAlreadyLinked = iTeroPatient.system === 'both';
		const isModified = isPatientModified(context);
		const needsLinking =
			!isAlreadyLinked &&
			(stateMatches('patientCard.invisalignPatient.selected') || stateMatches('patientCard.invisalignPatient.creating'));
		const isNewPatient = !iTeroPatient.id;
		const needsSaveItero = isModified || isNewPatient;

		if (!needsLinking && !needsSaveItero) {
			return of({ success: true });
		}

		this.reportSaved(context, stateMatches);

		const saveItero$: Observable<SavedIteroPatient | null | undefined> = needsSaveItero
			? (iTeroPatient.id ? this.api.updatePatient(iTeroPatient, iTeroPatient.id) : this.api.createPatient(iTeroPatient)).pipe(
					map(response => {
						const dto = response.patients?.length ? response.patients[0] : null;

						return dto
							? {
									uid: dto.uid,
									firstName: dto.firstName,
									lastName: dto.lastName,
									dateOfBirth: dto.DateOfBirth || '',
									gender: dto.gender,
									chartNumber: dto.chartNumber
								}
							: null;
					}),
					catchError(() => of(null))
				)
			: of(undefined);

		return saveItero$.pipe(
			switchMap(saved => {
				if (needsSaveItero && saved === null) {
					return of({ success: false } as SavePatientResult);
				}

				const iteroPatientId = saved?.uid ?? iTeroPatient.id ?? undefined;

				if (!needsLinking) {
					return of(this.buildSaveResult({ saved }));
				}

				return this.linkWithResolvedUid$(iTeroPatient, iteroPatientId, context.invisalignPatient?.id ?? undefined).pipe(
					map(link => this.buildSaveResult({ saved, link, iTeroPatient })),
					catchError(() => of({ success: false } as SavePatientResult))
				);
			})
		);
	}

	searchForMatchingPatients(context: PatientFormsContext): (sendBack: SendBack) => void {
		return sendBack => {
			if (!context.iTeroPatient?.firstName || !context.iTeroPatient?.lastName) {
				return;
			}

			// Patient already exists in iTero (has ID) and toggle is OFF — no need to search.
			// Emit NO_MATCHES_FOUND immediately so that `committed` can resolve to `yes`
			// (the invoke restarts on every self-transition, including toggle OFF after toggle ON).
			if (context.iTeroPatient.id && !context.invisalignToggle) {
				sendBack({ type: 'CARD.NO_MATCHES_FOUND' });

				return;
			}

			// Both patients already committed (link flow complete) — no need to search.
			if (context.invisalignPatient && !context.iTeroPatient.id) {
				sendBack({ type: 'CARD.NO_MATCHES_FOUND' });

				return;
			}

			const isUnmodifiedExisting = context.iTeroPatient.id && !isPatientModified(context);

			const dryRun$ = isUnmodifiedExisting
				? of([] as Patient[])
				: (context.iTeroPatient.id
						? this.api.updatePatient(context.iTeroPatient, context.iTeroPatient.id, true)
						: this.api.createPatient(context.iTeroPatient, true)
					).pipe(
						map((): Patient[] => []),
						catchError(err => {
							if (err.status === 409 && err.error?.conflictedPatients?.length) {
								return of((err.error.conflictedPatients as SavedPatientDto[]).map(d => mapDtoToPatient(d)));
							}

							return of([] as Patient[]);
						})
					);

			const search$ = context.invisalignToggle
				? this.api.searchPatients(context.iTeroPatient.firstName, context.iTeroPatient.lastName, 'Invisalign').pipe(
						map(res => (res.results ?? []).map(item => mapSearchItemToPatient(item))),
						catchError(() => of([] as Patient[]))
					)
				: of([] as Patient[]);

			const sub = forkJoin([dryRun$, search$]).subscribe(([iteroMatches, invisalignMatches]) => {
				const patientDob = context.iTeroPatient?.dateOfBirth;
				const filteredInvisalign = invisalignMatches.filter(
					c => !(patientDob && c.dateOfBirth && !this.dobFormat.isSameDoB(patientDob, c.dateOfBirth))
				);
				const searchIds = new Set(filteredInvisalign.map(p => p.id).filter(Boolean));
				const dedupedItero = iteroMatches.filter(p => !searchIds.has(p.id));
				const patients = [...dedupedItero, ...filteredInvisalign];

				if (patients.length) {
					sendBack({ type: 'CARD.MATCHES_FOUND', patients });
				} else {
					sendBack({ type: 'CARD.NO_MATCHES_FOUND' });
				}
			});

			return () => sub.unsubscribe();
		};
	}

	checkCrossSystemMatches(context: PatientFormsContext): (sendBack: SendBack) => void {
		return checkCrossSystemMatches(this.api, context, this.dobFormat);
	}

	searchPatientsInDB(context: PatientFormsContext, event: PatientFormsEvent): (sendBack: SendBack) => void {
		if (event.type !== 'SEARCH.QUERY_ENTERED') {
			return () => {};
		}

		const parts = event.query
			.trim()
			.split(/[\s,]+/)
			.filter(Boolean);
		const [first, second] = parts.map(p => `${p}*`);
		const sources = event.filter === 'iTero' ? 'ITero' : event.filter === 'Invisalign' ? 'Invisalign' : 'ITero,Invisalign';

		const empty: SearchPatientsResponse = { results: [], hasMore: false, pageSize: 0, offset: 0, total: 0 };
		const safe = (fn: string, ln: string) => this.api.searchPatients(fn, ln, sources).pipe(catchError(() => of(empty)));

		const requests = second ? [safe(first, second), safe(second, first)] : [safe(first, ''), safe('', first)];

		return sendBack => {
			const sub = forkJoin(requests)
				.pipe(
					map(responses => {
						const seen = new Set<string>();

						return responses
							.flatMap(r => r.results ?? [])
							.filter(item => {
								const key = item.iteroPatientId ?? item.invisalignPatientId;

								if (!key) {
									return true;
								}

								if (seen.has(key)) {
									return false;
								}

								seen.add(key);

								return true;
							})
							.sort((a, b) => ((b.dateUpdated ?? '') > (a.dateUpdated ?? '') ? 1 : -1))
							.map(item => mapSearchItemToPatient(item));
					}),
					catchError(() => of([] as Patient[]))
				)
				.subscribe(patients => sendBack({ type: 'SEARCH.RESULTS', patients }));

			return () => sub.unsubscribe();
		};
	}

	checkDuplicateIteroPatient(context: PatientFormsContext): (sendBack: SendBack) => void {
		return sendBack => {
			const patient = context.pendingIteroPatient || context.iTeroPatient;

			if (!patient) {
				sendBack({ type: 'EDITOR.NO_DUPLICATE' });

				return;
			}

			const sub = (patient.id ? this.api.updatePatient(patient, patient.id, true) : this.api.createPatient(patient, true)).subscribe({
				next: () => sendBack({ type: 'EDITOR.NO_DUPLICATE' }),
				error: err => {
					if (err.status === 409 && err.error?.conflictedPatients?.length) {
						const dup = mapDtoToPatient(err.error.conflictedPatients[0] as SavedPatientDto);

						sendBack({ type: 'EDITOR.DUPLICATE_FOUND', duplicate: dup });
					} else {
						sendBack({ type: 'EDITOR.NO_DUPLICATE' });
					}
				}
			});

			return () => sub.unsubscribe();
		};
	}

	loadExistingPatient(patientGuid: string, fallbackPatient$: Observable<RxPatientData | null>): Observable<Patient | null> {
		return this.api.getPatientById(patientGuid).pipe(
			map(response => {
				const item = response.results?.[0];

				return item ? mapSearchItemToPatient(item) : null;
			}),
			catchError(() => of(null)),
			switchMap(patient =>
				patient != null
					? of(patient)
					: fallbackPatient$.pipe(
							take(1),
							map(data => mapRxPatientDataToPatient(data))
						)
			)
		);
	}

	/**
	 * Dry-run createPatient (not update) to discover the real iTero UID — including for existing patients.
	 * The save API can silently swap UIDs; a 409 conflict is the reliable way to read the canonical one.
	 */
	private resolveIteroPatientId$(patient: Patient, fallbackUid: string | undefined): Observable<string | undefined> {
		return this.api.createPatient(patient, true).pipe(
			map(() => fallbackUid),
			catchError(err => {
				const conflicted = err?.status === 409 ? (err.error?.conflictedPatients?.[0] as SavedPatientDto | undefined) : undefined;

				return of(conflicted?.uid ?? fallbackUid);
			})
		);
	}

	/** Resolve UID via dry-run (Scanner: every attempt; non-Scanner: retries only), then link. */
	private linkWithResolvedUid$(
		patient: Patient,
		fallbackUid: string | undefined,
		invisalignPatientId: string | undefined
	): Observable<LinkSuccess> {
		const isScanner = this.isScannerHost();
		let attemptIndex = 0;

		const attempt$ = defer(() => {
			const shouldDryRun = isScanner || attemptIndex > 0;

			attemptIndex += 1;

			const resolvedUid$ = shouldDryRun ? this.resolveIteroPatientId$(patient, fallbackUid) : of(fallbackUid);

			return resolvedUid$.pipe(
				switchMap(resolvedIteroPatientId =>
					this.api
						.createLinkedPatient({
							iteroPatientId: resolvedIteroPatientId,
							invisalignPatientId,
							patient: {
								firstName: patient.firstName,
								lastName: patient.lastName,
								dateOfBirth: patient.dateOfBirth,
								gender: patient.gender,
								zipCode: '000000',
								chartNumber: patient.chartNumber ?? ''
							}
						})
						.pipe(
							map((response): LinkSuccess => {
								if (!response.Data) {
									throw new Error('create_linked_patient_missing_data');
								}

								return {
									linked: {
										iteroPatientId: response.Data.IteroPatientId,
										invisalignPatientId: response.Data.InvisalignPatientId
									},
									resolvedIteroPatientId
								};
							})
						)
				)
			);
		});

		const initialDryRunDelayMs = isScanner ? CREATE_LINKED_PATIENT_INITIAL_DRY_RUN_DELAY_MS : 0;

		return timer(initialDryRunDelayMs).pipe(
			switchMap(() =>
				attempt$.pipe(
					retry({
						count: CREATE_LINKED_PATIENT_RETRY_INTERVALS_MS.length,
						delay: (error, retryCount) => timer(CREATE_LINKED_PATIENT_RETRY_INTERVALS_MS[retryCount - 1])
					})
				)
			)
		);
	}

	/** Scanner host Rx API URLs contain "RxEditor" (same signal as patient-link API path selection). */
	private isScannerHost(): boolean {
		return this.config.getHostRxApiUrl().includes('RxEditor');
	}

	private buildSaveResult(params: {
		saved: SavedIteroPatient | null | undefined;
		link?: LinkSuccess;
		iTeroPatient?: Patient;
	}): SavePatientResult {
		const { saved, link, iTeroPatient } = params;
		const result: SavePatientResult = { success: true };
		const resolvedUid = link?.resolvedIteroPatientId;

		if (saved) {
			result.savedIteroPatient = resolvedUid ? { ...saved, uid: resolvedUid } : saved;
		} else if (iTeroPatient && resolvedUid && resolvedUid !== iTeroPatient.id) {
			result.savedIteroPatient = {
				uid: resolvedUid,
				firstName: iTeroPatient.firstName,
				lastName: iTeroPatient.lastName,
				dateOfBirth: iTeroPatient.dateOfBirth,
				chartNumber: iTeroPatient.chartNumber ?? ''
			};
		}

		if (link?.linked) {
			result.linkedPatient = link.linked;
		}

		return result;
	}

	private reportSaved(context: PatientFormsContext, stateMatches: (path: string) => boolean): void {
		void this.bi.reporter?.trackStage({
			object: BI_FEATURE,
			stage: 'saved',
			previous: '',
			description: '',
			trigger: 'user',
			customAttributes: buildSaveDimensions(context, stateMatches)
		});
	}
}
